import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Car,
    Check,
    ChevronDown,
    Globe2,
    MapPin,
    Plus,
    Ruler,
    Settings as SettingsIcon,
    Trash2,
    Wallet,
    X,
} from 'lucide-react';
import { useTranslation } from '../useTranslation';
import { CarInfo } from './CarTripService';
import AuthService, { type UserProfile } from './AuthService';
import { readStoredSettings, writeStoredSettings, writeLanguageToStorage } from '../utils/settingsStorage';
import {
    DEFAULT_RESIDENCE_COUNTRY,
    countryLabel,
    sortedResidenceCountries,
} from '../utils/residenceCountries';

export type Settings = {
    cars: CarInfo[];
    currency: string;
    language: string;
    distanceUnit: string;
    theme: 'light' | 'dark';
    /** ISO country of residence / passport nationality for visa rules. */
    residenceCountry: string;
};

type SettingsSection = 'preferences' | 'garage';

function profileToSettings(profile: UserProfile, fallback: Settings): Settings {
    const p = profile.preferences;
    return {
        language: profile.language || fallback.language,
        currency: p?.currency ?? fallback.currency,
        distanceUnit: p?.distanceUnit ?? fallback.distanceUnit,
        theme: p?.theme ?? fallback.theme,
        residenceCountry: (p?.residenceCountry || fallback.residenceCountry || DEFAULT_RESIDENCE_COUNTRY).toUpperCase(),
        cars: p?.cars?.length ? p.cars : fallback.cars,
    };
}

function profileSelectedCarIndex(profile: UserProfile, fallback: number): number {
    const idx = profile.preferences?.selectedCarIndex;
    if (typeof idx === 'number' && idx >= 0) return idx;
    return fallback;
}

const FUEL_OPTIONS = [
    { value: 'Бензин', ru: true },
    { value: 'Дизель', ru: true },
    { value: 'Газ', ru: true },
    { value: 'Электро', ru: true },
] as const;

function Segmented<T extends string>({
    value,
    options,
    onChange,
}: {
    value: T;
    options: { value: T; label: string }[];
    onChange: (v: T) => void;
}) {
    return (
        <div className="inline-flex p-1 rounded-xl bg-stone-100 dark:bg-slate-800 border border-stone-200/80 dark:border-slate-600/80">
            {options.map(opt => {
                const on = opt.value === value;
                return (
                    <button
                        key={opt.value}
                        type="button"
                        onClick={() => onChange(opt.value)}
                        className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${
                            on
                                ? 'bg-white dark:bg-slate-700 text-stone-900 dark:text-white shadow-sm'
                                : 'text-stone-500 dark:text-slate-400 hover:text-stone-800 dark:hover:text-white'
                        }`}
                    >
                        {opt.label}
                    </button>
                );
            })}
        </div>
    );
}

function ChipGroup<T extends string>({
    value,
    options,
    onChange,
}: {
    value: T;
    options: { value: T; label: string }[];
    onChange: (v: T) => void;
}) {
    return (
        <div className="flex flex-wrap gap-2">
            {options.map(opt => {
                const on = opt.value === value;
                return (
                    <button
                        key={opt.value}
                        type="button"
                        onClick={() => onChange(opt.value)}
                        className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-medium border transition-all ${
                            on
                                ? 'border-blue-500 bg-blue-50 text-blue-800 dark:bg-blue-950/50 dark:text-blue-100 dark:border-blue-500 shadow-sm'
                                : 'border-stone-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-stone-600 dark:text-slate-300 hover:border-stone-300 dark:hover:border-slate-500'
                        }`}
                    >
                        {on ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : null}
                        {opt.label}
                    </button>
                );
            })}
        </div>
    );
}

const inputCl =
    'w-full h-10 px-3 rounded-xl border border-stone-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-stone-900 dark:text-white text-sm placeholder:text-stone-400 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-shadow';

function CountrySelect({
    value,
    language,
    onChange,
}: {
    value: string;
    language: string;
    onChange: (code: string) => void;
}) {
    const [open, setOpen] = useState(false);
    const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    const btnRef = useRef<HTMLButtonElement>(null);
    const list = useMemo(() => sortedResidenceCountries(language), [language]);
    const selected = value || DEFAULT_RESIDENCE_COUNTRY;
    const label = countryLabel(selected, language);

    const placeMenu = useCallback(() => {
        const el = btnRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        setMenuPos({ top: r.bottom + 6, left: r.left, width: r.width });
    }, []);

    useEffect(() => {
        if (!open) {
            setMenuPos(null);
            return;
        }
        placeMenu();
        const onDoc = (e: MouseEvent) => {
            if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        const onReposition = () => placeMenu();
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        window.addEventListener('resize', onReposition);
        window.addEventListener('scroll', onReposition, true);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('resize', onReposition);
            window.removeEventListener('scroll', onReposition, true);
        };
    }, [open, placeMenu]);

    return (
        <div ref={rootRef} className="relative">
            <button
                ref={btnRef}
                type="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => setOpen(v => !v)}
                className={`group w-full h-11 px-3.5 rounded-xl border text-left text-sm font-medium transition-all inline-flex items-center justify-between gap-2 ${
                    open
                        ? 'border-blue-500 bg-white dark:bg-slate-800 ring-2 ring-blue-500/25 text-stone-900 dark:text-white shadow-sm'
                        : 'border-stone-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-stone-900 dark:text-white hover:border-stone-300 dark:hover:border-slate-500 hover:bg-stone-50/80 dark:hover:bg-slate-800/80'
                }`}
            >
                <span className="inline-flex items-center gap-2.5 min-w-0">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-rose-50 dark:bg-rose-950/40 text-[11px] font-bold tracking-wide text-rose-600 dark:text-rose-300">
                        {selected}
                    </span>
                    <span className="truncate">{label}</span>
                </span>
                <ChevronDown
                    className={`h-4 w-4 shrink-0 text-stone-400 transition-transform duration-200 ${open ? 'rotate-180 text-blue-500' : 'group-hover:text-stone-600 dark:group-hover:text-slate-300'}`}
                />
            </button>

            {open && menuPos && (
                <ul
                    role="listbox"
                    style={{
                        position: 'fixed',
                        top: menuPos.top,
                        left: menuPos.left,
                        width: menuPos.width,
                    }}
                    className="z-[80] max-h-56 overflow-y-auto rounded-xl border border-stone-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-xl shadow-stone-900/15 dark:shadow-black/50 py-1 overscroll-contain"
                >
                    {list.map(c => {
                        const on = c.code === selected;
                        const name = language.toLowerCase().startsWith('ru') ? c.ru : c.en;
                        return (
                            <li key={c.code} role="option" aria-selected={on}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        onChange(c.code);
                                        setOpen(false);
                                    }}
                                    className={`w-full px-3 py-2 text-left text-sm inline-flex items-center gap-2.5 transition-colors ${
                                        on
                                            ? 'bg-blue-50 text-blue-800 dark:bg-blue-950/50 dark:text-blue-100'
                                            : 'text-stone-700 dark:text-slate-200 hover:bg-stone-50 dark:hover:bg-slate-700/80'
                                    }`}
                                >
                                    <span className="w-7 shrink-0 text-[11px] font-bold tabular-nums text-stone-400 dark:text-slate-500">
                                        {c.code}
                                    </span>
                                    <span className="flex-1 min-w-0 truncate font-medium">{name}</span>
                                    {on ? <Check className="h-3.5 w-3.5 shrink-0 text-blue-600 dark:text-blue-300" strokeWidth={3} /> : null}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

export const SettingsForm: React.FC<{
    showSettings: boolean;
    setShowSettings: (show: boolean) => void;
    settings: Settings;
    setSettings: React.Dispatch<React.SetStateAction<Settings>>;
    selectedCarIndex: number;
    setSelectedCarIndex: React.Dispatch<React.SetStateAction<number>>;
}> = ({ showSettings, setShowSettings, settings, setSettings, selectedCarIndex, setSelectedCarIndex }) => {
    const { t } = useTranslation(settings.language);
    const [section, setSection] = useState<SettingsSection>('preferences');
    const [draft, setDraft] = useState<Settings>(settings);
    const [draftCarIndex, setDraftCarIndex] = useState(selectedCarIndex);

    useEffect(() => {
        if (!showSettings) return;
        setDraft(settings);
        setDraftCarIndex(selectedCarIndex);
        setSection('preferences');
    }, [showSettings, settings, selectedCarIndex]);

    const persistSettings = async (next: Settings, carIndex: number) => {
        writeStoredSettings(next);
        writeLanguageToStorage(next.language);
        try {
            await AuthService.updatePreferences({
                language: next.language,
                currency: next.currency,
                distanceUnit: next.distanceUnit,
                theme: next.theme,
                residenceCountry: next.residenceCountry,
                cars: next.cars,
                selectedCarIndex: carIndex,
            });
        } catch (error) {
            console.warn('[Settings] Failed to save settings to server:', error);
            throw error;
        }
    };

    const updateCar = (index: number, patch: Partial<CarInfo>) => {
        setDraft(prev => {
            const cars = [...(prev.cars || [])];
            cars[index] = { ...cars[index], ...patch };
            return { ...prev, cars };
        });
    };

    const addCar = () => {
        setDraft(prev => ({
            ...prev,
            cars: [
                ...(prev.cars || []),
                {
                    make: '',
                    fuelConsumption: 7.0,
                    fuelType: 'Бензин',
                    year: new Date().getFullYear(),
                },
            ],
        }));
        setDraftCarIndex((draft.cars || []).length);
    };

    const removeCar = (index: number) => {
        if (!draft.cars || draft.cars.length <= 1) return;
        setDraft(prev => {
            const cars = prev.cars.filter((_, i) => i !== index);
            return { ...prev, cars };
        });
        setDraftCarIndex(prev => {
            if (index < prev) return prev - 1;
            if (index === prev) return Math.max(0, prev - 1);
            return Math.min(prev, (draft.cars?.length || 2) - 2);
        });
    };

    const handleSave = async () => {
        setSettings(draft);
        setSelectedCarIndex(draftCarIndex);
        localStorage.setItem('selectedCarIndex', draftCarIndex.toString());
        try {
            await persistSettings(draft, draftCarIndex);
            setShowSettings(false);
        } catch {
            // keep modal open so user can retry
        }
    };

    if (!showSettings) return null;

    const fuelLabel = (value: string) => {
        switch (value) {
            case 'Бензин': return t.gasoline;
            case 'Дизель': return t.diesel;
            case 'Газ': return t.gas;
            case 'Электро': return t.electric;
            default: return value;
        }
    };

    const sections: { id: SettingsSection; label: string; icon: typeof Globe2 }[] = [
        { id: 'preferences', label: t.settingsPreferences, icon: Globe2 },
        { id: 'garage', label: t.settingsGarage, icon: Car },
    ];

    return (
        <div
            className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center bg-stone-900/45 dark:bg-black/55 backdrop-blur-md p-0 sm:p-4"
            role="dialog"
            aria-modal="true"
        >
            <div
                className="relative w-full sm:max-w-2xl lg:max-w-3xl max-h-[100dvh] sm:max-h-[min(88dvh,680px)] flex flex-col rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl border border-white/40 dark:border-slate-600/60 bg-gradient-to-b from-slate-50 via-white to-stone-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-950"
            >
                <div className="relative shrink-0 px-3 sm:px-4 pt-2.5 pb-2 border-b border-stone-200/80 dark:border-slate-700/80">
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(59,130,246,0.12),_transparent_55%)] pointer-events-none" />
                    <div className="relative flex items-center gap-3 pr-10">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-600/25">
                            <SettingsIcon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-base font-semibold text-stone-900 dark:text-white tracking-tight">{t.settings}</h2>
                            <p className="text-xs text-stone-500 dark:text-slate-400 truncate">{t.appDescription}</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowSettings(false)}
                        className="absolute top-2.5 right-2.5 z-10 p-1.5 rounded-lg text-stone-500 hover:text-stone-800 hover:bg-white/70 dark:text-slate-400 dark:hover:text-white dark:hover:bg-slate-800"
                        aria-label={t.cancel}
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="shrink-0 px-3 sm:px-4 pt-2">
                    <div className="flex gap-1 p-0.5 rounded-xl bg-stone-100/90 dark:bg-slate-800/80 border border-stone-200/70 dark:border-slate-700/70">
                        {sections.map(s => {
                            const on = section === s.id;
                            return (
                                <button
                                    key={s.id}
                                    type="button"
                                    onClick={() => setSection(s.id)}
                                    className={`flex-1 inline-flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-lg text-sm font-semibold transition-all ${
                                        on
                                            ? 'bg-white dark:bg-slate-700 text-blue-700 dark:text-blue-300 shadow-sm'
                                            : 'text-stone-500 dark:text-slate-400 hover:text-stone-800 dark:hover:text-white'
                                    }`}
                                >
                                    <s.icon className="h-4 w-4" />
                                    {s.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto px-3 sm:px-4 py-3 space-y-3">
                    {section === 'preferences' && (
                        <>
                            <section className="rounded-xl border border-stone-200/80 dark:border-slate-700/70 bg-white/70 dark:bg-slate-800/40 p-3 space-y-2">
                                <div className="flex items-center gap-2 text-stone-800 dark:text-slate-100">
                                    <Globe2 className="h-4 w-4 text-blue-500" />
                                    <h3 className="text-sm font-semibold">{t.language}</h3>
                                </div>
                                <Segmented
                                    value={draft.language as 'ru-RU' | 'en-US'}
                                    onChange={language => setDraft(prev => ({ ...prev, language }))}
                                    options={[
                                        { value: 'ru-RU', label: t.russian },
                                        { value: 'en-US', label: t.english },
                                    ]}
                                />
                            </section>

                            <section className="rounded-xl border border-stone-200/80 dark:border-slate-700/70 bg-white/70 dark:bg-slate-800/40 p-3 space-y-2">
                                <div className="flex items-center gap-2 text-stone-800 dark:text-slate-100">
                                    <MapPin className="h-4 w-4 text-rose-500" />
                                    <h3 className="text-sm font-semibold">{t.settingsResidenceCountry}</h3>
                                </div>
                                <p className="text-xs text-stone-500 dark:text-slate-400 -mt-1">{t.settingsResidenceCountryHint}</p>
                                <CountrySelect
                                    value={draft.residenceCountry || DEFAULT_RESIDENCE_COUNTRY}
                                    language={draft.language}
                                    onChange={code => setDraft(prev => ({ ...prev, residenceCountry: code }))}
                                />
                            </section>

                            <section className="rounded-xl border border-stone-200/80 dark:border-slate-700/70 bg-white/70 dark:bg-slate-800/40 p-3 space-y-2">
                                <div className="flex items-center gap-2 text-stone-800 dark:text-slate-100">
                                    <Wallet className="h-4 w-4 text-emerald-500" />
                                    <h3 className="text-sm font-semibold">{t.currency}</h3>
                                </div>
                                <ChipGroup
                                    value={draft.currency as '₽' | '$' | '€'}
                                    onChange={currency => setDraft(prev => ({ ...prev, currency }))}
                                    options={[
                                        { value: '₽', label: t.ruble },
                                        { value: '$', label: t.dollar },
                                        { value: '€', label: t.euro },
                                    ]}
                                />
                            </section>

                            <section className="rounded-xl border border-stone-200/80 dark:border-slate-700/70 bg-white/70 dark:bg-slate-800/40 p-3 space-y-2">
                                <div className="flex items-center gap-2 text-stone-800 dark:text-slate-100">
                                    <Ruler className="h-4 w-4 text-amber-500" />
                                    <h3 className="text-sm font-semibold">{t.distanceUnit}</h3>
                                </div>
                                <Segmented
                                    value={draft.distanceUnit as 'km' | 'miles'}
                                    onChange={distanceUnit => setDraft(prev => ({ ...prev, distanceUnit }))}
                                    options={[
                                        { value: 'km', label: t.kilometers },
                                        { value: 'miles', label: t.milesUnit },
                                    ]}
                                />
                            </section>
                        </>
                    )}

                    {section === 'garage' && (
                        <>
                            <p className="text-xs text-stone-500 dark:text-slate-400 -mt-1">{t.settingsGarageHint}</p>
                            <div className="space-y-2">
                                {(draft.cars || []).map((car, index) => {
                                    const isDefault = index === draftCarIndex;
                                    return (
                                        <div
                                            key={index}
                                            className={`rounded-xl border p-2.5 space-y-2 transition-all ${
                                                isDefault
                                                    ? 'border-blue-400 dark:border-blue-500 bg-blue-50/50 dark:bg-blue-950/20 shadow-sm'
                                                    : 'border-stone-200/80 dark:border-slate-700/70 bg-white/70 dark:bg-slate-800/40'
                                            }`}
                                        >
                                            <div className="flex items-center justify-between gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => setDraftCarIndex(index)}
                                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${
                                                        isDefault
                                                            ? 'bg-blue-600 text-white'
                                                            : 'bg-stone-100 dark:bg-slate-700 text-stone-600 dark:text-slate-300 hover:bg-stone-200 dark:hover:bg-slate-600'
                                                    }`}
                                                >
                                                    {isDefault ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
                                                    {isDefault ? t.defaultCar : `${t.car} ${index + 1}`}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => removeCar(index)}
                                                    disabled={(draft.cars || []).length <= 1}
                                                    className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-40 disabled:cursor-not-allowed"
                                                    title={t.deleteCar}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>

                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                                <div className="col-span-2 min-w-0">
                                                    <label className="block text-[10px] font-semibold uppercase tracking-wide text-stone-500 dark:text-slate-400 mb-1">{t.carBrand}</label>
                                                    <input
                                                        type="text"
                                                        value={car.make || ''}
                                                        onChange={e => updateCar(index, { make: e.target.value })}
                                                        placeholder="Toyota"
                                                        className={inputCl}
                                                    />
                                                </div>
                                                <div className="min-w-0">
                                                    <label className="block text-[10px] font-semibold uppercase tracking-wide text-stone-500 dark:text-slate-400 mb-1">{t.carYear}</label>
                                                    <input
                                                        type="number"
                                                        value={car.year || ''}
                                                        onChange={e => updateCar(index, { year: parseInt(e.target.value, 10) || new Date().getFullYear() })}
                                                        className={`${inputCl} input-no-spinner`}
                                                        min={1970}
                                                        max={2100}
                                                    />
                                                </div>
                                                <div className="min-w-0">
                                                    <label className="block text-[10px] font-semibold uppercase tracking-wide text-stone-500 dark:text-slate-400 mb-1">{t.fuelConsumptionRate}</label>
                                                    <input
                                                        type="number"
                                                        step="0.1"
                                                        value={car.fuelConsumption ?? 0}
                                                        onChange={e => updateCar(index, { fuelConsumption: parseFloat(e.target.value) || 0 })}
                                                        className={`${inputCl} input-no-spinner`}
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-[10px] font-semibold uppercase tracking-wide text-stone-500 dark:text-slate-400 mb-1.5">{t.fuelType}</label>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {FUEL_OPTIONS.map(opt => {
                                                        const on = (car.fuelType || 'Бензин') === opt.value;
                                                        return (
                                                            <button
                                                                key={opt.value}
                                                                type="button"
                                                                onClick={() => updateCar(index, { fuelType: opt.value })}
                                                                className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                                                                    on
                                                                        ? 'border-emerald-500 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-500'
                                                                        : 'border-stone-200 dark:border-slate-600 text-stone-600 dark:text-slate-300 hover:border-stone-300'
                                                                }`}
                                                            >
                                                                {fuelLabel(opt.value)}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <button
                                type="button"
                                onClick={addCar}
                                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border border-dashed border-stone-300 dark:border-slate-600 text-stone-600 dark:text-slate-300 hover:border-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50/50 dark:hover:bg-blue-950/20 transition-colors"
                            >
                                <Plus className="h-4 w-4" />
                                {t.addCar}
                            </button>
                        </>
                    )}
                </div>

                <div className="shrink-0 px-4 sm:px-5 py-3 border-t border-stone-200/80 dark:border-slate-700/80 bg-white/90 dark:bg-slate-900/95 backdrop-blur-sm flex justify-end pb-[max(0.75rem,env(safe-area-inset-bottom))]">
                    <button
                        type="button"
                        onClick={() => void handleSave()}
                        className="w-full sm:w-auto sm:min-w-[10rem] px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md shadow-blue-600/20"
                    >
                        {t.save}
                    </button>
                </div>
            </div>
        </div>
    );
};

export const useSettingsService = () => {
    const [settings, setSettings] = useState<Settings>({
        cars: [
            {
                make: 'Toyota',
                fuelConsumption: 7.5,
                fuelType: 'Бензин',
                year: 2020,
            },
        ],
        currency: '₽',
        language: 'ru-RU',
        distanceUnit: 'km',
        theme: 'light',
        residenceCountry: DEFAULT_RESIDENCE_COUNTRY,
    });
    const [selectedCarIndex, setSelectedCarIndex] = useState(0);
    const [showSettings, setShowSettings] = useState(false);

    useEffect(() => {
        const parsedSettings = readStoredSettings();

        if (Object.keys(parsedSettings).length > 0) {
            try {
                if (parsedSettings.car && !parsedSettings.cars) {
                    parsedSettings.cars = [parsedSettings.car];
                    delete parsedSettings.car;
                }

                if (!parsedSettings.theme) {
                    parsedSettings.theme = 'light';
                }
                // Migrate old city-based "origin" setting away; default residence country
                if (!parsedSettings.residenceCountry) {
                    parsedSettings.residenceCountry = DEFAULT_RESIDENCE_COUNTRY;
                }
                delete parsedSettings.origin;

                setSettings(prev => ({ ...prev, ...parsedSettings } as Settings));

                const savedCarIndex = localStorage.getItem('selectedCarIndex');
                if (savedCarIndex && Array.isArray(parsedSettings.cars)) {
                    const index = parseInt(savedCarIndex, 10);
                    if (!isNaN(index) && index >= 0 && index < parsedSettings.cars.length) {
                        setSelectedCarIndex(index);
                    }
                }
            } catch (error) {
                console.error('Error parsing settings from localStorage:', error);
            }
        }
    }, []);

    const syncSettingsFromServer = useCallback(async () => {
        const profile = await AuthService.fetchProfile();
        if (!profile) return;
        setSettings((prev) => {
            const merged = profileToSettings(profile, prev);
            writeStoredSettings(merged);
            return merged;
        });
        setSelectedCarIndex((prev) => {
            const idx = profileSelectedCarIndex(profile, prev);
            localStorage.setItem('selectedCarIndex', String(idx));
            return idx;
        });
    }, []);

    return {
        settings,
        setSettings,
        selectedCarIndex,
        setSelectedCarIndex,
        showSettings,
        setShowSettings,
        syncSettingsFromServer,
        /** @deprecated */ syncLanguageFromServer: syncSettingsFromServer,
    };
};
